import {
  Tabulator,
  PageModule,
  SortModule,
  InteractionModule,
  FormatModule,
  ResizeTableModule,
  TooltipModule,
  PopupModule,
  FilterModule,
  DownloadModule,
  ExportModule,
  AccessorModule,
  DataTreeModule,
  ColumnCalcsModule,
  AjaxModule,
  GroupRowsModule,
  MoveRowsModule,
} from 'tabulator-tables';

// Side-effect import: replaces MoveRows' drop-target decision on the module
// prototype (corrected maths, direction-aware row targeting, clone-leading-edge
// downward trigger). Must come before `registerModule` below so every table
// built from this registration uses it. See the file for the upstream lines and
// the reasons; `spec/system/internal/guides_spec.rb` has two examples that fail
// if this import is ever dropped.
import './move_rows_drop_fix';

// Registers every Tabulator module our component touches. Lives in its own
// side-effect file because `Tabulator.extendModule(name, ...)` silently
// fails if the named module hasn't been registered yet — so the sorters
// and layout files import this first before extending. Importing this
// from anywhere is idempotent: re-registering a module is a no-op inside
// Tabulator's `ModuleBinder`.
Tabulator.registerModule([
  PageModule,
  SortModule,
  InteractionModule,
  FormatModule,
  // ResizeTable: makes Tabulator's `autoResize` option (default true) actually
  // fire — without this module registered it's silently a no-op, and columns
  // don't re-fit when the container changes width.
  ResizeTableModule,
  // Tooltip: powers the `tooltip` column option (set on `columnDefaults` in
  // the controller) so truncated cell content is revealable on hover.
  TooltipModule,
  // Popup: powers `headerPopup` + `headerPopupIcon`, the mechanism behind our
  // per-column filter dropdown. The module inserts a button in the header
  // and renders the popup content into <body> so it escapes the column's
  // `overflow: hidden`; we translate the column's `filter:` config into those
  // options in `tabulator_table/filters.js`.
  PopupModule,
  // Filter: provides `setFilter` / `clearFilter`. We drive it ourselves via
  // a function filter built from per-column state (see `applyFilters` in
  // the controller); the column config's `filter:` key is our Ruby-side
  // shorthand for the popup UI, not Tabulator's `headerFilter` feature.
  FilterModule,
  // Download + Export + Accessor: power `tabulator.download(format, filename)`
  // used by the `csv_export: true` opt-in to wire up the "Download CSV"
  // button. Download does the CSV serialisation + blob creation + synthetic
  // anchor click; it depends on Export's `generateExportList`, so both must
  // be registered together (Tabulator silently no-ops if Export isn't
  // there). Accessor registers the `accessorDownload` column option we set
  // on `columnDefaults` to strip cell HTML before serialisation — without
  // it the option is silently ignored and the CSV contains raw markup.
  DownloadModule,
  ExportModule,
  AccessorModule,
  // DataTree: powers expandable parent/child rows. Activated per-table by
  // the controller when `extractRowData` finds at least one row marked
  // with `data-child-row` in the source haml — children are grouped under
  // the preceding parent into Tabulator's `_children` array.
  DataTreeModule,
  // ColumnCalcs: powers the bottom footer row. Activated per-table by the
  // controller when `extractRowData` finds a `<tr data-footer-row>` in
  // the source haml — each cell becomes a static `bottomCalc` on the
  // matching column.
  ColumnCalcsModule,
  // Ajax: powers `ajaxURL` + `ajaxURLGenerator` + remote sort/pagination
  // modes, used by `UI::TabulatorTableComponent` (when `remote: true`)
  // to fetch each page of rows from a JSON endpoint.
  AjaxModule,
  // GroupRows: powers `groupBy` + `groupValues` + `groupHeader`. Activated
  // per-table by the controller when the component opts in with
  // `grouped_rows: true`; inert (and toggle-free) for every other table.
  GroupRowsModule,
  // MoveRows: powers `movableRows` + the `rowHandle` column option, and the
  // `rowMoved` event the controller turns into a `tabulator-table:row-moved`
  // DOM event. Activated per-table by the component's `movable_rows:` opt-in;
  // `movableRows` defaults false, so the module is inert everywhere else.
  MoveRowsModule,
]);
